import { createSlice } from '@reduxjs/toolkit';

import {
  apiGetAudienceLists,
  apiCreateAudienceList,
  apiUpdateAudienceList,
  apiDeleteAudienceList,
} from '@/mastodon/api/audience_lists';
import type {
  ApiAudienceListJSON,
  ApiCreateAudienceListPayload,
  ApiUpdateAudienceListPayload,
} from '@/mastodon/api_types/audience_lists';
import { createAppAsyncThunk } from '@/mastodon/store/typed_functions';

type QueryStatus = 'idle' | 'loading' | 'error';

interface AudienceListIdsQuery {
  ids: string[];
  status: QueryStatus;
}

interface AudienceListsState {
  audienceLists: Record<string, ApiAudienceListJSON>;
  myAudienceLists: AudienceListIdsQuery;
}

const initialAudienceListsState: AudienceListsState = {
  audienceLists: {},
  myAudienceLists: { ids: [], status: 'idle' },
};

export const fetchMyAudienceLists = createAppAsyncThunk(
  'audienceLists/fetchMine',
  () => apiGetAudienceLists(),
);

export const createAudienceList = createAppAsyncThunk(
  'audienceLists/create',
  (payload: ApiCreateAudienceListPayload) => apiCreateAudienceList(payload),
);

export const updateAudienceList = createAppAsyncThunk(
  'audienceLists/update',
  (payload: ApiUpdateAudienceListPayload) => apiUpdateAudienceList(payload),
);

export const deleteAudienceList = createAppAsyncThunk(
  'audienceLists/delete',
  async (id: string) => {
    await apiDeleteAudienceList(id);
    return id;
  },
);

const importAudienceLists = (
  state: AudienceListsState,
  audienceLists: ApiAudienceListJSON[] | ApiAudienceListJSON,
) => {
  const list = Array.isArray(audienceLists) ? audienceLists : [audienceLists];
  list.forEach((audienceList) => {
    state.audienceLists[audienceList.id] = audienceList;
  });
};

const audienceListsSlice = createSlice({
  name: 'audienceLists',
  initialState: initialAudienceListsState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchMyAudienceLists.pending, (state) => {
        state.myAudienceLists.status = 'loading';
      })
      .addCase(fetchMyAudienceLists.fulfilled, (state, action) => {
        importAudienceLists(state, action.payload);
        state.myAudienceLists = {
          ids: action.payload.map((audienceList) => audienceList.id),
          status: 'idle',
        };
      })
      .addCase(fetchMyAudienceLists.rejected, (state) => {
        state.myAudienceLists.status = 'error';
      })
      .addCase(createAudienceList.fulfilled, (state, action) => {
        importAudienceLists(state, action.payload);
        state.myAudienceLists.ids.push(action.payload.id);
      })
      .addCase(updateAudienceList.fulfilled, (state, action) => {
        importAudienceLists(state, action.payload);
      })
      .addCase(deleteAudienceList.fulfilled, (state, action) => {
        Reflect.deleteProperty(state.audienceLists, action.payload);
        state.myAudienceLists.ids = state.myAudienceLists.ids.filter(
          (id) => id !== action.payload,
        );
      });
  },
});

export const audienceLists = audienceListsSlice.reducer;
